<svelte:options
  customElement={{
    tag: "oc-snackbar-v1",
    extend: window.__components.extend({ asyncInit: true }),
    props: {
      open: { type: "Boolean" },
      message: { type: "String" },
      id: { type: "String", reflect: true },
      autoDismiss: { type: "String", attribute: "auto-dismiss" },
      forceReplaceExistingInstance: {
        type: "Boolean",
        attribute: "force-replace-existing-instance",
      },
    },
  }}
/>

<script lang="ts">
  import { srSpeak } from "@otto-ec/global-resources/accesibility";
  import {
    OnAnimationFrame,
    onElementAnimated,
    OnImmediate,
    useDomAnimationHelper,
  } from "@otto-ec/otto-components-utils/svelte/useDomAnimationHelper";
  import { useTimeout } from "@otto-ec/otto-components-utils/svelte/useTimeout";
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { safeId } from "@otto-ec/otto-components-utils/utils/safeId";
  import { onDestroy } from "svelte";
  import { innerHeight, innerWidth, scrollX, scrollY } from "svelte/reactivity/window";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { canUseHtmlPopoverApi } from "../../../common/utils/canUseHtmlPopoverApi";
  import { track } from "../../../common/utils/track";
  import { useRestoreFocus } from "../../../common/utils/useRestoreFocus.svelte";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import { calculateSnackbarAdditionalSpacing } from "./lib/positionHelper";
  import type { Events, Props } from "./SnackbarV1.types.js";

  let {
    open = false,
    message,
    id = safeId(),
    autoDismiss = true,
    forceReplaceExistingInstance = false,
  }: Props = $props();

  const Host = $host();
  const slots = useSlots(Host);
  const dispatch = useEventDispatcher<Events>(Host);

  let DURATION = 5000;
  let isHovered = $state(false);
  let isFocused = $state(false);
  let snackbarElement: HTMLElement | undefined = $state();

  /*                                                  */
  const isInsideSheet = $derived(!!Host.closest("oc-sheet-v1"));
  const usePopover = $derived(canUseHtmlPopoverApi);

  const restoreFocus = useRestoreFocus(Host);

  /*                                               */
  const [visibilityState, setVisibility] = useDomAnimationHelper({
    open: [OnImmediate, onElementAnimated(() => Host.shadowRoot?.children)],
    animate: [OnAnimationFrame, OnImmediate],
  });

  /*                  */
  $effect(() => {
    /*                 */
    if (open === visibilityState.open) return;

    /*                                             */
    /*                                                    */
    if (
      !dispatch(open ? "oc-snackbar-open" : "oc-snackbar-close", undefined, {
        alias: [open ? "oc-open" : "oc-close"],
      })
    ) {
      /*                                           */
      open = visibilityState.open;
      return;
    }

    /*                                                         */
    setVisibility(open);
  });

  /*                                            */
  $effect(() => {
    if (open) srSpeak(`Hinweis: ${message}`, { type: "assertive" });
  });

  const handleClose = (): void => {
    open = false;
    restoreFocus();
    Host.blur();
  };

  const handleESCKey = (event: KeyboardEvent): void => {
    if (event.code === "Escape") handleClose();
  };

  /*                                                                        */
  $effect(() => {
    if (`${autoDismiss}` !== "false" && open && !isHovered && !isFocused)
      return useTimeout(handleClose, DURATION);
  });

  /*                                                                                       */
  /*                                         */
  /*                                                                       */
  /*                                                */
  /*                                     */
  if (forceReplaceExistingInstance) {
    requestAnimationFrame(() => {
      document.querySelectorAll(`[id="${id}"]`).forEach((el) => el.remove());
    });
  }

  /*                                                                                              */
  /*                                                                                                       */
  if (import.meta.env.STORYBOOK) {
    /*                                                                                    */
    onDestroy(handleClose);
  }

  /*                                                                                                     */
  const { bottom, left, sheetBottom, sheetLeft, sheetWidth } = $derived.by(() => {
    const zero = { bottom: 0, left: 0, sheetBottom: 0, sheetLeft: 0, sheetWidth: 0 };
    if (!snackbarElement) return zero;

    /*                                          */
    track(innerHeight.current, innerWidth.current, scrollY.current, scrollX.current);
    /*                                                                                */
    track(open, visibilityState.open);

    /*                                                                    */
    /*                                                              */
    /*                                                                         */
    if (isInsideSheet && visibilityState.open) {
      const sheetHost = Host.closest("oc-sheet-v1");
      const footer = sheetHost?.shadowRoot?.querySelector(".sheet__footer");
      const sheetInner = sheetHost?.shadowRoot?.querySelector(".sheet__sheet");
      const anchorRect = (footer ?? sheetInner)?.getBoundingClientRect();

      if (anchorRect) {
        return {
          bottom: 0,
          left: 0,
          /*                                                                */
          /*                                                                          */
          sheetBottom: footer
            ? window.innerHeight - anchorRect.top + 16
            : window.innerHeight - anchorRect.bottom + 16,
          sheetLeft: anchorRect.left + anchorRect.width / 2,
          sheetWidth: anchorRect.width,
        };
      }
      return zero;
    }

    if (usePopover) return zero;
    return { ...zero, ...calculateSnackbarAdditionalSpacing(snackbarElement) };
  });

  $effect(() => {
    /*                                                                         */
    if (usePopover && snackbarElement) {
      snackbarElement.showPopover();
    }
  });
</script>

<svelte:document onkeydown={handleESCKey} />

{#if visibilityState.open}
  <div
    popover={usePopover ? "manual" : undefined}
    role="status"
    class="snackbar"
    class:snackbar--open={visibilityState.animate}
    class:snackbar--has-actions={slots.actions}
    class:snackbar--as-popover={usePopover}
    class:snackbar--in-sheet={isInsideSheet}
    onmouseenter={() => (isHovered = true)}
    onmouseleave={() => (isHovered = false)}
    onfocusin={() => (isFocused = true)}
    onfocusout={() => (isFocused = false)}
    aria-hidden={visibilityState.open ? "false" : "true"}
    bind:this={snackbarElement}
    style:--snackbar-bottom-spacing={`${bottom}px`}
    style:--snackbar-left-spacing={`${left}px`}
    style:--snackbar-sheet-bottom={`${sheetBottom}px`}
    style:--snackbar-sheet-left={`${sheetLeft}px`}
    style:--snackbar-sheet-width={`${sheetWidth}px`}
  >
    <div class="snackbar__body-actions-container">
      <div class="snackbar__body">
        {message}
      </div>

      {#if slots.actions}
        <div class="snackbar__actions" style:display={visibilityState.open ? "block" : "none"}>
          <slot name="actions" />
        </div>
      {/if}

      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <oc-icon-button-v3
        class="snackbar__close-button"
        style:display={visibilityState.open ? "block" : "none"}
        variant="transparent-inverted"
        icon="close"
        size={/*                      */ "50"}
        onclick={handleClose}
        oc-aria-label="Hinweis schließen"
      ></oc-icon-button-v3>
    </div>
  </div>

  <DefaultStyles />
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    $icon-button-width: calc(#{tokens.$oc-base-dimension-relative-32} + 16px);

    :host {
      position: absolute;
    }

    .snackbar {
      background-color: tokens.$oc-component-snackbar-background-color;
      border: none; /*                          */
      border-radius: tokens.$oc-component-snackbar-border-radius;
      border-color: tokens.$oc-component-snackbar-background-color;
      filter: tokens.$oc-component-snackbar-shadow;
      min-height: 2rem;
      padding: tokens.$oc-component-snackbar-spacing-y $icon-button-width
        tokens.$oc-component-snackbar-spacing-y tokens.$oc-component-snackbar-spacing-left;
      width: calc(
        var(--oc-snackbar-container-width, 100vw) - #{tokens.$oc-component-snackbar-spacing-left} -
          #{tokens.$oc-component-snackbar-outer-spacing-x} * 2 -
          #{tokens.$oc-component-snackbar-gap-x} - $icon-button-width
      );

      &,
      &:popover-open {
        /*                                                              */
        bottom: calc(
          #{tokens.$oc-component-snackbar-outer-spacing-bottom} +
            var(--snackbar-bottom-spacing, 0px)
        );
        left: calc(50% + var(--snackbar-left-spacing, 0px));
        position: fixed;
        z-index: 9999;
      }

      &.snackbar--in-sheet,
      &.snackbar--in-sheet:popover-open {
        position: fixed;
        bottom: var(--snackbar-sheet-bottom, 0px);
        left: var(--snackbar-sheet-left, 50%);
        right: auto;
        top: auto;
        z-index: 9999;

        width: calc(
          var(--snackbar-sheet-width, 100%) - #{tokens.$oc-component-snackbar-outer-spacing-x} * 2
        );
      }

      &--as-popover {
        margin: 0;
        inset: unset;
      }

      &__body-actions-container {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: tokens.$oc-component-snackbar-text-action-gap-y
          tokens.$oc-component-snackbar-text-action-min-gap-x;
        padding: tokens.$oc-component-snackbar-text-action-spacing-y 0;
      }

      &__body {
        flex: 1 1 auto;
        min-width: 0;
        color: tokens.$oc-component-snackbar-copy-color;
        font: tokens.$oc-component-snackbar-copy-font;
        padding-right: calc(#{tokens.$oc-component-snackbar-gap-x} / 2);
      }

      &__actions {
        padding-right: calc(#{tokens.$oc-component-snackbar-gap-x} / 2);
      }

      &__close-button {
        position: absolute;
        top: tokens.$oc-semantic-spacing-50;
        right: tokens.$oc-semantic-spacing-50;
        padding: 0;
        background: none;
        border: none;
      }

      &--has-actions {
        .snackbar__body {
          padding-right: 0;
        }
      }
    }

    .snackbar {
      transition:
        transform tokens.$oc-component-snackbar-disappear-duration
          tokens.$oc-component-snackbar-disappear-easing,
        opacity tokens.$oc-component-snackbar-disappear-duration
          tokens.$oc-component-snackbar-disappear-easing;
      opacity: 0;
      transform: translate(-50%, 100%);
    }

    .snackbar--open {
      transition:
        transform tokens.$oc-component-snackbar-appear-duration
          tokens.$oc-component-snackbar-appear-easing,
        opacity tokens.$oc-component-snackbar-appear-duration
          tokens.$oc-component-snackbar-appear-easing;
      opacity: 1;
      transform: translate(-50%, 0);
    }

    @include mixins.breakpoint-from-m(false, false) {
      .snackbar {
        max-width: max-content;
      }
    }
  }
</style>
